import { useState, type ReactNode } from 'react';
import { NavLink, Route, Routes, useLocation } from 'react-router';
import { BrandMark } from './components/brand.js';
import { useSession } from './auth/session.js';
import {
  IconArchive,
  IconBulb,
  IconClose,
  IconDashboard,
  IconExternal,
  IconGear,
  IconLayers,
  IconLink,
  IconList,
  IconLogout,
  IconMenu,
  IconCloudSun,
  IconMatter,
  IconPlug,
  IconRadio,
  IconServer,
} from './components/icons.js';
import { Verzia } from './components/version.js';
import { Dashboard } from './routes/dashboard.js';
import { Devices } from './routes/devices.js';
import { DeviceDetail } from './routes/device-detail.js';
import { Integrations } from './routes/integrations.js';
import { Loxone } from './routes/loxone.js';
import { Mappings } from './routes/mappings.js';
import { Tuya } from './routes/tuya.js';
import { Zigbee } from './routes/zigbee.js';
import { Radios } from './routes/radia.js';
import { Rozhrania } from './routes/rozhrania.js';
import { MatterPage } from './routes/matter.js';
import { Weather } from './routes/pocasie.js';
import { NotFound } from './routes/misc.js';
import { Logs } from './routes/logy.js';
import { Backups } from './routes/zalohy.js';
import { System } from './routes/system.js';

/*
 * Skrupina aplikacie.
 *
 * Rozlozenie: na desktope trvala bocna navigacia, na mobile zasuvka.
 * Dovod pre bocnu navigaciu: poloziek je strnast a na sirku by sa uz
 * zalamovali; zvislo su citatelne na jeden pohlad a zostava miesto na
 * obsah, ktory je tu podstatny.
 */

const NAVIGACIA = [
  { to: '/', label: 'Prehľad', icon: IconDashboard, end: true },
  { to: '/zariadenia', label: 'Zariadenia', icon: IconBulb },
  { to: '/integracie', label: 'Integrácie', icon: IconLayers },
  { to: '/tuya', label: 'Tuya', icon: IconPlug },
  { to: '/zigbee', label: 'Zigbee', icon: IconLayers },
  { to: '/matter', label: 'Matter', icon: IconMatter },
  { to: '/radia', label: 'Rádiá', icon: IconRadio },
  { to: '/pocasie', label: 'Počasie', icon: IconCloudSun },
  { to: '/loxone', label: 'Loxone', icon: IconServer },
  { to: '/mapovania', label: 'Mapovania', icon: IconLink },
  { to: '/logy', label: 'Logy', icon: IconList },
  { to: '/rozhrania', label: 'Rozhrania', icon: IconExternal },
  { to: '/system', label: 'Systém', icon: IconGear },
  { to: '/zalohy', label: 'Zálohy', icon: IconArchive },
] as const;

function NavigaciaObsah({ onNavigate }: { readonly onNavigate?: () => void }): ReactNode {
  return (
    <ul className="flex flex-col gap-0.5">
      {NAVIGACIA.map((item) => {
        const Ikona = item.icon;
        return (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={'end' in item ? item.end : false}
              onClick={onNavigate}
              className={({ isActive }) =>
                [
                  'flex items-center gap-3 rounded-(--radius-control) px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-(--color-accent-soft) font-medium text-(--color-accent)'
                    : 'text-(--color-text-muted) hover:bg-(--color-surface-sunken) hover:text-(--color-text)',
                ].join(' ')
              }
            >
              <Ikona className="size-5 shrink-0" />
              {item.label}
            </NavLink>
          </li>
        );
      })}
    </ul>
  );
}

function Znacka(): ReactNode {
  return (
    <div className="flex items-center gap-2.5">
      <span className="text-(--color-accent)">
        <BrandMark />
      </span>
      <span className="text-base font-semibold tracking-tight text-(--color-text)">LoxLink</span>
    </div>
  );
}

function Ucet(): ReactNode {
  const { user, logout } = useSession();
  if (!user) return null;
  return (
    <div className="flex items-center justify-between gap-2 border-t border-(--color-border) px-3 pt-3">
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium text-(--color-text)">{user.username}</span>
        <span className="text-xs text-(--color-text-subtle)">
          {user.role === 'admin' ? 'Správca' : 'Čítanie'}
        </span>
      </div>
      <button
        type="button"
        aria-label="Odhlásiť sa"
        title="Odhlásiť sa"
        onClick={() => void logout()}
        className="inline-flex size-9 items-center justify-center rounded-(--radius-control) text-(--color-text-muted) transition-colors hover:bg-(--color-surface-sunken) hover:text-(--color-text)"
      >
        <IconLogout />
      </button>
    </div>
  );
}

export function App(): ReactNode {
  const [otvorene, setOtvorene] = useState(false);
  const location = useLocation();

  return (
    <div className="flex min-h-full flex-col lg:flex-row">
      {/* Mobilna hlavicka */}
      <header className="flex items-center justify-between gap-3 border-b border-(--color-border) bg-(--color-surface-raised) px-4 py-3 lg:hidden">
        <Znacka />
        <button
          type="button"
          aria-label={otvorene ? 'Zavrieť navigáciu' : 'Otvoriť navigáciu'}
          aria-expanded={otvorene}
          onClick={() => setOtvorene((o) => !o)}
          className="inline-flex size-9 items-center justify-center rounded-(--radius-control) text-(--color-text-muted) hover:bg-(--color-surface-sunken)"
        >
          {otvorene ? <IconClose /> : <IconMenu />}
        </button>
      </header>

      {otvorene && (
        <nav
          aria-label="Hlavná navigácia"
          className="border-b border-(--color-border) bg-(--color-surface-raised) px-3 py-3 lg:hidden"
        >
          <NavigaciaObsah onNavigate={() => setOtvorene(false)} />
          <div className="mt-3 flex flex-col gap-2">
            <Verzia />
            <Ucet />
          </div>
        </nav>
      )}

      {/*
       * Bocna navigacia na desktope.
       *
       * Drzi sa obrazovky (`sticky` na vysku okna) a roluje sa v nej len
       * zoznam odkazov. Ucet, odhlasenie a verzia tak zostavaju dole na
       * dohlad - predtym boli na konci STRANKY, takze pri dlhom zozname
       * zariadeni sa k nim muselo rolovat (ziadost zadavatela
       * 2026-09-24).
       */}
      <aside className="hidden w-60 shrink-0 border-r border-(--color-border) bg-(--color-surface-raised) lg:sticky lg:top-0 lg:flex lg:h-dvh lg:flex-col">
        <div className="shrink-0 px-5 py-5">
          <Znacka />
        </div>
        {/* `min-h-0` je nutne: bez neho sa polozka flexu nezmensi a rolovalo by sa cele okno. */}
        <nav aria-label="Hlavná navigácia" className="min-h-0 flex-1 overflow-y-auto px-3">
          <NavigaciaObsah />
        </nav>
        <div className="flex shrink-0 flex-col gap-2 pb-4">
          <Verzia />
          <Ucet />
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* `key` podla cesty: pri prepnuti obrazovky sa obsah nacita nanovo. */}
        <main
          className="mx-auto w-full max-w-5xl flex-1 px-4 py-6 sm:px-6 sm:py-8"
          key={location.pathname}
        >
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/zariadenia" element={<Devices />} />
            <Route path="/zariadenia/:deviceId" element={<DeviceDetail />} />
            <Route path="/integracie" element={<Integrations />} />
            <Route path="/tuya" element={<Tuya />} />
            <Route path="/zigbee" element={<Zigbee />} />
            <Route path="/radia" element={<Radios />} />
            <Route path="/matter" element={<MatterPage />} />
            <Route path="/pocasie" element={<Weather />} />
            <Route path="/loxone" element={<Loxone />} />
            <Route path="/mapovania" element={<Mappings />} />
            <Route path="/logy" element={<Logs />} />
            <Route path="/rozhrania" element={<Rozhrania />} />
            <Route path="/system" element={<System />} />
            <Route path="/zalohy" element={<Backups />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>

        <footer className="mx-auto w-full max-w-5xl px-4 pb-6 text-xs text-(--color-text-subtle) sm:px-6">
          LoxLink je nezávislý open-source projekt. Nie je spojený so spoločnosťou Loxone ani s
          1Home.
        </footer>
      </div>
    </div>
  );
}
